/**
 * Keyboard shortcuts cheat-sheet (point 18) — opened with '?', closed with
 * Esc or the backdrop. Bilingual list of every global console shortcut.
 */
import { useEffect } from 'react';
import { useI18n, type Localized } from '@servicesai/ui';
import { S } from '../strings.js';

const SHORTCUTS: { keys: string; label: Localized }[] = [
  { keys: 'g q', label: S.queues },
  { keys: 'g a', label: S.agents },
  { keys: 'g h', label: { en: 'Manzili governance', ar: 'حوكمة منزلي' } },
  { keys: 'g d', label: S.dashboard },
  { keys: '/  ⌘K', label: { en: 'Open search', ar: 'فتح البحث' } },
  { keys: 'A  C  R', label: { en: 'Case file: agree / correct / return the highlighted section', ar: 'ملف الحالة: موافقة / تصحيح / إعادة القسم المحدد' } },
  { keys: '?', label: { en: 'Show this shortcuts list', ar: 'عرض قائمة الاختصارات هذه' } },
  { keys: 'Esc', label: { en: 'Close dialogs', ar: 'إغلاق النوافذ' } },
];

export function ShortcutsOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { l } = useI18n();

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-[100] flex items-start justify-center bg-aeblack-900/45 px-4 pt-[14vh] backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={l({ en: 'Keyboard shortcuts', ar: 'اختصارات لوحة المفاتيح' })}
        className="w-full max-w-md overflow-hidden rounded-xl border border-aeblack-200 bg-white shadow-2xl"
      >
        <div className="border-b border-aeblack-100 px-5 py-4">
          <h2 className="text-sm font-extrabold text-ink">{l({ en: 'Keyboard shortcuts', ar: 'اختصارات لوحة المفاتيح' })}</h2>
          <p className="mt-0.5 text-[11px] text-ink-faint">
            {l({ en: 'Press ? anytime to show this list.', ar: 'اضغط ? في أي وقت لعرض هذه القائمة.' })}
          </p>
        </div>
        <ul className="divide-y divide-aeblack-50 px-5 py-1">
          {SHORTCUTS.map((shortcut) => (
            <li key={shortcut.keys} className="flex items-center justify-between gap-3 py-2.5">
              <span className="text-xs font-semibold text-ink">{l(shortcut.label)}</span>
              <kbd
                dir="ltr"
                className="rounded-md border border-aeblack-200 bg-aeblack-50 px-2 py-1 font-mono text-[10px] font-bold text-ink-soft"
              >
                {shortcut.keys}
              </kbd>
            </li>
          ))}
        </ul>
        <footer className="border-t border-aeblack-100 bg-aeblack-50 px-5 py-2 text-[10px] text-ink-faint">
          <kbd className="font-mono">Esc</kbd> {l({ en: 'to close', ar: 'للإغلاق' })}
        </footer>
      </section>
    </div>
  );
}
